<script lang="ts">
  import { activeTab, chatOpen, settingsOpen, uploadsOpen, profileFormOpen, user, authReady, dataReady, activeProfileId, profiles, ndaAccepted } from './lib/stores';
  import AppHeader from './components/AppHeader.svelte';
  import { t } from './lib/i18n';
  import TabBar from './components/TabBar.svelte';
  import Footer from './components/Footer.svelte';
  import SignIn from './views/SignIn.svelte';
  import AgreementGate from './components/AgreementGate.svelte';
  import AccessGate from './components/AccessGate.svelte';
  import Toast from './components/Toast.svelte';
  import ReportBanner from './components/ReportBanner.svelte';
  import PlotModal from './components/PlotModal.svelte';
  import { accessState, refreshAccess, claimSelf, ACCESS_ENFORCED } from './lib/accessControl';
  import { plotRequest } from './lib/plotRequest';
  import { initLogic } from './lib/logicEngine';
  import Lazy from './components/Lazy.svelte';
  import IdleTimeout from './components/IdleTimeout.svelte';
  import DemoGate from './components/DemoGate.svelte';
  import { isDemoRoute, demoActive } from './lib/demoMode';

  // Demo experience: /demo shows a test-code gate; a valid code seeds the synthetic
  // dataset (stores.startDemo) and flips $demoActive, after which the normal gate
  // below falls straight through to the app running on demo data (no Firebase).
  // data-demo on <html> switches the accent to the distinct demo blue (see app.css)
  // for the whole route — gate and app.
  const onDemoRoute = isDemoRoute();
  if (onDemoRoute) { try { document.documentElement.setAttribute('data-demo', ''); } catch (_) {} }

  // Heavy views are code-split (loaded on first use) so the initial bundle is
  // just the shell + auth + landing. The Dashboard drags in Three.js (~500 KB),
  // so it's the biggest win — it only loads once the Dashboard tab is opened.

  // Layout rule: "app" views fill the viewport and never page-scroll (they
  // manage their own internal layout). "document" views scroll between the
  // fixed top chrome and the footer.
  const FIT = new Set(['dashboard', 'imaging']);
  const fit = $derived(!$settingsOpen && FIT.has($activeTab));

  // Mount the persistent Dashboard pane only after the tab is first opened, then
  // keep it mounted (so the 3D scene doesn't reload on every tab switch).
  let dashSeen = $state(false);
  $effect(() => { if ($activeTab === 'dashboard') dashSeen = true; });

  // Access gate (Phase 2) — inert while ACCESS_ENFORCED is false. Re-checks the
  // signed-in user's claims; shows the "by invitation / pending" screen if enforced.
  const accessGated = $derived(ACCESS_ENFORCED && ($accessState === 'pending' || $accessState === 'code_required' || $accessState === 'blocked'));
  // Backend access-claim + logic engine touch Firebase — skip them entirely in the
  // no-login demo.
  $effect(() => { if ($demoActive) return; const u = $user; void claimSelf(u).then(() => refreshAccess(u)); });
  // Subscribe the logic engine to logic/main once signed in, so automations are
  // ready before any trigger (e.g. filing a report) fires.
  $effect(() => { if ($user && !$demoActive) initLogic(); });

  // Boot watchdog: if the "Loading…" spinner lingers (a Firestore read wedged on
  // iOS Safari, or a stalled auth redirect), reveal a manual reload after a grace
  // period so the user is never trapped on an endless spinner.
  let bootStuck = $state(false);
  $effect(() => {
    const spinning = (!$authReady || ($user && !$dataReady))
      || ($ndaAccepted === null && !!$user && $authReady && $dataReady && !accessGated);
    if (!spinning) { bootStuck = false; return; }
    const t = setTimeout(() => (bootStuck = true), 12000);
    return () => clearTimeout(t);
  });
</script>

{#snippet bootSpinner()}
  <div class="boot">
    <span class="faint">Loading…</span>
    {#if bootStuck}
      <button class="reload-btn" onclick={() => location.reload()}>Still loading — tap to reload</button>
    {/if}
  </div>
{/snippet}

<!-- Auto sign-out after 10 min of inactivity (protects ePHI on shared/unattended devices).
     Not in the demo (no real session to protect; a warning modal would just annoy testers). -->
{#if $user && !$demoActive}<IdleTimeout />{/if}

{#if onDemoRoute && !$demoActive}
  <DemoGate />
{:else if !$authReady || ($user && !$dataReady)}
  {@render bootSpinner()}
{:else if !$user}
  <SignIn />
{:else if accessGated}
  <AccessGate state={$accessState} />
{:else if $ndaAccepted === null}
  {@render bootSpinner()}
{:else if $ndaAccepted === false}
  <AgreementGate />
{:else if !$activeProfileId}
  {#if $profiles.length > 0}
    <Lazy load={() => import('./views/ProfilePicker.svelte')} />
  {:else}
    <Lazy load={() => import('./views/ProfileForm.svelte')} props={{ onboarding: true }} />
  {/if}
{:else}
<div class="app">
  <AppHeader />
  <TabBar />
  <main class="content" class:fit>
    <!-- Dashboard stays mounted (hidden when inactive) so the 3D / Sketchfab
         viewer doesn't reload every time you switch tabs. Loaded lazily on first open. -->
    <div class="dash-pane" class:show={$activeTab === 'dashboard' && !$settingsOpen}>
      {#if dashSeen}<Lazy load={() => import('./views/Dashboard.svelte')} />{/if}
    </div>
    {#if $settingsOpen}
      <Lazy load={() => import('./views/Settings.svelte')} />
    {:else if $activeTab === 'profile'}
      <Lazy load={() => import('./views/HealthProfile.svelte')} />
    {:else if $activeTab === 'labs'}
      <Lazy load={() => import('./views/Labs.svelte')} />
    {:else if $activeTab === 'imaging'}
      <Lazy load={() => import('./views/Imaging.svelte')} />
    {:else if $activeTab === 'procedures'}
      <Lazy load={() => import('./views/Procedures.svelte')} />
    {:else if $activeTab === 'notes'}
      <Lazy load={() => import('./views/Notes.svelte')} />
    {/if}
  </main>
  <Footer />

  <!-- empowered assistant: a modal overlay (opened from the header), not a page. -->
  {#if $chatOpen}
    <Lazy load={() => import('./views/Chat.svelte')} />
  {/if}
  {#if $uploadsOpen}
    <Lazy load={() => import('./views/Uploads.svelte')} />
  {/if}
  {#if $profileFormOpen}
    <Lazy load={() => import('./views/ProfileForm.svelte')} />
  {/if}

  {#if $plotRequest}
    <PlotModal
      title={$plotRequest.title}
      unit={$plotRequest.unit}
      series={$plotRequest.series}
      clinical={$plotRequest.clinical ?? false}
      ranges={$plotRequest.ranges ?? false}
      presets={$plotRequest.presets}
      defaultRange={$plotRequest.defaultRange}
      connect={$plotRequest.connect ?? false}
      padFrac={$plotRequest.padFrac}
      timeAxis={true}
      onclose={() => plotRequest.set(null)} />
  {/if}

  <Toast />
  <ReportBanner />
  {#if $demoActive}<div class="demo-badge" title={$t('demo.badgeTitle')}>{$t('demo.badge')}</div>{/if}
</div>
{/if}

<style>
  /* Device-aware shell. Phone = full-width column; iPad/desktop widen so grids,
     tables and imaging use the space. Reading-heavy views constrain their own
     line length (see .content :global rule below). */
  .app {
    display: flex;
    flex-direction: column;
    height: 100dvh;
    width: 100%;
    max-width: 760px;             /* phone / small */
    margin: 0 auto;
    background: var(--bg);
  }
  @media (min-width: 768px)  { .app { max-width: 940px; } }    /* iPad portrait+ */
  @media (min-width: 1180px) { .app { max-width: 1160px; } }   /* iPad landscape / laptop */
  @media (min-width: 1440px) { .app { max-width: 1320px; } }   /* desktop */

  /* Document views: the only scroller, between fixed chrome + footer. */
  .content {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: var(--space-4) var(--space-4) var(--space-6);
    /* Positioning context for in-view overlays (e.g. the imaging report modal),
       so they stay BELOW the top chrome and never slide under the menu bar. */
    position: relative;
  }
  @media (min-width: 768px)  { .content { padding: var(--space-5) var(--space-6) var(--space-8); } }
  @media (min-width: 1180px) { .content { padding: var(--space-6) var(--space-8) var(--space-10); } }
  /* App views (Dashboard, Imaging): fill, no page scroll. */
  .content.fit {
    overflow: hidden;
    display: flex;
    padding: var(--space-3) var(--space-4);
  }
  @media (min-width: 1180px) { .content.fit { padding: var(--space-4) var(--space-6); } }
  /* the single fit-view child stretches to fill */
  .content.fit > :global(*) { flex: 1; min-height: 0; width: 100%; }
  .boot { height: 100dvh; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--space-4); background: var(--bg); }
  .reload-btn {
    padding: var(--space-2) var(--space-4);
    border: 1px solid var(--line, rgba(128, 128, 128, 0.4));
    border-radius: 999px;
    background: transparent;
    color: var(--fg, inherit);
    font: inherit; font-size: 0.85rem; cursor: pointer;
  }
  .reload-btn:hover { background: rgba(128, 128, 128, 0.08); }
  /* Persistent dashboard pane: hidden when another tab is active. */
  .dash-pane { display: none; }
  .dash-pane.show { display: flex; flex: 1; min-height: 0; width: 100%; }
  /* Demo watermark — a quiet, always-visible reminder that this is sample data. */
  .demo-badge {
    position: fixed; left: 50%; bottom: calc(var(--safe-bottom) + var(--space-3)); transform: translateX(-50%);
    z-index: var(--z-toast); pointer-events: none;
    padding: 4px var(--space-3); border-radius: var(--radius-pill);
    background: var(--accent-soft); border: 1px solid var(--accent);
    color: var(--accent-2); font-size: var(--fs-2xs); letter-spacing: 0.08em; text-transform: uppercase; font-weight: 600;
    backdrop-filter: blur(6px);
  }
</style>
